import BigNumber from 'bignumber.js'
import { match as match_ } from 'ts-pattern'

import { BigNumber as BigNumber_, Equivalence } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency/model'
import { Provider } from '@root/entities/provider/model'

import type { PairAddress } from './PairAddress'
import type { PairModel } from '../atoms/reatom-pair'
import type {
  PumpFunBuyAccounts,
  PumpSwapAccounts,
  RaydiumClmmAccounts,
  RaydiumCpmmAccounts,
  RaydiumV4Accounts,
} from '@blade/blockchain-types'
import type { Network } from '@root/entities/network/model'
import type {
  SecureStoragePoolAccounts,
  SecureStoragePumpFunPoolAccounts,
  SecureStoragePumpSwapPoolAccounts,
  SecureStorageRaydiumCpmmPoolAccounts,
  SecureStorageRaydiumV4PoolAccounts,
} from '@root/features/secure-storage/secure-storage-service'
import type { ServicesApi } from '@root/shared/api'

export type PairReusedShort = PairModel & {
  traders24h: number
}

export interface Price {
  priceNative: BigNumber
  priceUsd: BigNumber
}

export interface PairHolders {
  count: number
  holding: BigNumber
}

export type Accounts = {
  buy: AccountsDto | undefined
  sell: AccountsDto | undefined
}

export interface Fees {
  totalFees: BigNumber
  gasFees: BigNumber
  totalBundleTipsFees: BigNumber
  poolFee: BigNumber | undefined
}

export interface PairInsiders {
  count: number
  holding: BigNumber
}
export interface PairPriceChartEntry {
  timestamp: Date
  price: {
    open: BigNumber
    high: BigNumber
    low: BigNumber
    close: BigNumber
  }
}

export interface PairMinimal {
  address: PairAddress
  quote: CurrencyModel
  base: CurrencyModel
  createdAt: Date | undefined
  network: Network.Network
  provider: Provider.Provider
}

export const match = (query: string) => (pair: PairMinimal) => {
  const resolvedQuery = query.trim().toLowerCase()
  return (
    CurrencyModel.match(resolvedQuery)(pair.quote) ||
    pair.address.toLowerCase().includes(resolvedQuery)
  )
}

export type AccountsDto =
  | RaydiumClmmAccounts
  | PumpSwapAccounts
  | PumpFunBuyAccounts
  | RaydiumCpmmAccounts
  | RaydiumV4Accounts

export const poolAccountsToSecureStoragePoolAccounts = (
  provider: Provider.Provider,
  poolAccounts: AccountsDto,
) => {
  return match_(provider)
    .returnType<SecureStoragePoolAccounts | undefined>()
    .with(Provider.pumpFun, () => {
      const apiAccounts = poolAccounts as PumpFunBuyAccounts
      const secureStorageAccounts: SecureStoragePumpFunPoolAccounts = {
        creatorVault: apiAccounts.creatorVault,
      }
      return secureStorageAccounts
    })
    .with(Provider.pumpSwap, () => {
      const apiAccounts = poolAccounts as PumpSwapAccounts
      const secureStorageAccounts: SecureStoragePumpSwapPoolAccounts = {
        baseMint: apiAccounts.baseMint,
        quoteMint: apiAccounts.quoteMint,
        baseTokenProgram: apiAccounts.baseTokenProgram,
        quoteTokenProgram: apiAccounts.quoteTokenProgram,
        coinCreatorVaultAccount: apiAccounts.coinCreatorVaultAccount,
        coinCreatorVaultAuthority: apiAccounts.coinCreatorVaultAuthority,
        coinCreator: apiAccounts.coinCreator,
        protocolFeeRecipient: apiAccounts.protocolFeeRecipient,
        protocolFeeRecipientAccount: apiAccounts.protocolFeeRecipientAccount,
        poolBaseAccount: apiAccounts.poolBaseAccount,
        poolQuoteAccount: apiAccounts.poolQuoteAccount,
      }
      return secureStorageAccounts
    })
    .with(Provider.raydiumCpmm, () => {
      const apiAccounts = poolAccounts as RaydiumCpmmAccounts
      const secureStorageAccounts: SecureStorageRaydiumCpmmPoolAccounts = {
        inputVault: apiAccounts.inputVault,
        outputVault: apiAccounts.outputVault,
        inputMint: apiAccounts.inputMint,
        outputMint: apiAccounts.outputMint,
        ammConfig: apiAccounts.ammConfig,
        authority: apiAccounts.authority,
        inputTokenProgram: apiAccounts.inputTokenProgram,
        outputTokenProgram: apiAccounts.outputTokenProgram,
        observationState: apiAccounts.observationState,
      }
      return secureStorageAccounts
    })
    .with(Provider.raydiumV4, () => {
      const apiAccounts = poolAccounts as RaydiumV4Accounts
      const secureStorageAccounts: SecureStorageRaydiumV4PoolAccounts = {
        ammAuthority: apiAccounts.ammAuthority,
        ammOpenOrders: apiAccounts.ammOpenOrders || '',
        ammTargetOrders: apiAccounts.ammTargetOrders || '',
        poolCoinTokenAccount: apiAccounts.poolCoinTokenAccount,
        poolPcTokenAccount: apiAccounts.poolPcTokenAccount,
        serumProgramId: apiAccounts.serumProgram || '',
        serumMarket: apiAccounts.serumMarket || '',
        serumBids: apiAccounts.serumBids || '',
        serumAsks: apiAccounts.serumAsks || '',
        serumEventQueue: apiAccounts.serumEventQueue || '',
        serumCoinVaultAccount: apiAccounts.serumCoinVaultAccount || '',
        serumPcVaultAccount: apiAccounts.serumPcVaultAccount || '',
        serumVaultSigner: apiAccounts.serumVaultSigner || '',
      }
      return secureStorageAccounts
    })
    .otherwise(() => undefined)
}

export const feesFromDto = (dto: ServicesApi.PairDto): Fees => {
  return {
    totalFees: BigNumber(dto.fees.totalFees),
    gasFees: BigNumber(dto.fees.totalPriorityFees),
    totalBundleTipsFees: BigNumber(dto.fees.totalBundleTipFees),
    poolFee: dto.poolFee === undefined ? undefined : BigNumber(dto.poolFee),
  }
}

export const eqHolders: Equivalence.Equivalence<PairHolders> =
  Equivalence.Struct({
    count: Equivalence.Number,
    holding: BigNumber_.equivalence,
  })
